import React, { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable, ColumnDefinition } from "@/components/ui/data-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  Plus,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  ArrowRightLeft,
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  Download,
  MapPin,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { HelpButton } from "@/components/help-button";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import { getTransfers, deleteTransfer, initializeMockData, getTransferItems, updateTransferStatus, getTransferConfig, duplicateTransfer } from "@/lib/transfer-storage";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StockTransferDrawerDetails } from "@/components/stock-transfer-drawer-details";
import { TransferItem } from "@/pages/stock-transfers-step2";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";

// Mock data types
export interface StockTransfer {
  id: string;
  reference: string;
  status: "draft" | "in-transit" | "completed" | "cancelled";
  createdAt: string;
  createdBy: string;
  dispatchedAt?: string;
  expectedArrivalDate?: string;
  completedAt?: string;
  itemCount: number;
  totalQuantity: number;
  notes?: string;
}

// Mock data removed - now using storage

const getStatusColor = (status: StockTransfer["status"]) => {
  switch (status) {
    case "draft":
      return "bg-gray-500/10 text-gray-500 border-gray-500/20";
    case "in-transit":
      return "bg-blue-500/10 text-blue-500 border-blue-500/20";
    case "completed":
      return "bg-green-500/10 text-green-500 border-green-500/20";
    case "cancelled":
      return "bg-red-500/10 text-red-500 border-red-500/20";
    default:
      return "bg-gray-500/10 text-gray-500 border-gray-500/20";
  }
};

const getStatusIcon = (status: StockTransfer["status"]) => {
  switch (status) {
    case "draft":
      return <Edit className="h-3 w-3" />;
    case "in-transit":
      return <ArrowRightLeft className="h-3 w-3" />;
    case "completed":
      return <CheckCircle2 className="h-3 w-3" />;
    case "cancelled":
      return <XCircle className="h-3 w-3" />;
    default:
      return null;
  }
};

const formatStatus = (status: StockTransfer["status"]) => {
  switch (status) {
    case "in-transit":
      return "In Transit";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

export function StockTransfersPage() {
  const { navigateTo } = useNavigation();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("stock-transfers");
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "draft" | "in-transit" | "completed">("all");
  const [transfers, setTransfers] = useState<StockTransfer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<StockTransfer | null>(null);
  const [selectedTransferItems, setSelectedTransferItems] = useState<TransferItem[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    column: keyof StockTransfer | null;
    direction: "asc" | "desc";
  }>({ column: null, direction: "asc" });

  // Function to reload transfers
  const reloadTransfers = React.useCallback(() => {
    setIsLoading(true);
    try {
      const loadedTransfers = getTransfers();
      setTransfers(loadedTransfers);
    } catch (error) {
      console.error("Error loading transfers:", error);
      toast.error("Failed to load transfers");
      setTransfers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load transfers from storage on mount
  useEffect(() => {
    // Initialize mock data if needed
    initializeMockData();
    
    // Load transfers
    reloadTransfers();
  }, [reloadTransfers]);

  // Reload transfers when they are updated
  useEffect(() => {
    const handleTransfersUpdated = () => {
      reloadTransfers();
    };

    // Listen for custom event when transfers are saved/updated/deleted
    window.addEventListener("transfersUpdated", handleTransfersUpdated);

    return () => {
      window.removeEventListener("transfersUpdated", handleTransfersUpdated);
    };
  }, [reloadTransfers]);

  // Filter transfers based on search, location, and tab
  const filteredTransfers = useMemo(() => {
    let filtered = transfers;

    // Filter by tab
    if (activeTab !== "all") {
      filtered = filtered.filter((transfer) => transfer.status === activeTab);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (transfer) =>
          transfer.reference.toLowerCase().includes(query) ||
          transfer.createdBy.toLowerCase().includes(query) ||
          transfer.notes?.toLowerCase().includes(query)
      );
    }

    // Filter by location
    if (locationFilter) {
      const locationQuery = locationFilter.toLowerCase();
      filtered = filtered.filter((transfer) => {
        const items = getTransferItems(transfer.id);
        return items.some(
          (item) =>
            item.sourceLocation.toLowerCase().includes(locationQuery) ||
            item.destinationLocation.toLowerCase().includes(locationQuery)
        );
      });
    }

    return filtered;
  }, [transfers, searchQuery, locationFilter, activeTab]);

  // Apply sorting
  const sortedTransfers = useMemo(() => {
    if (!sortConfig.column) return filteredTransfers;

    return [...filteredTransfers].sort((a, b) => {
      const aValue = a[sortConfig.column!];
      const bValue = b[sortConfig.column!];

      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [filteredTransfers, sortConfig]);

  // Apply pagination
  const paginatedTransfers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedTransfers.slice(startIndex, endIndex);
  }, [sortedTransfers, currentPage, itemsPerPage]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const drafts = transfers.filter((t) => t.status === "draft").length;
    const inTransit = transfers.filter((t) => t.status === "in-transit").length;
    const completed = transfers.filter((t) => t.status === "completed").length;
    const totalItems = transfers.reduce((sum, t) => sum + t.itemCount, 0);

    return { drafts, inTransit, completed, totalItems };
  }, [transfers]);

  const handleCreateTransfer = () => {
    navigateTo("stock-transfers/create/step1");
  };

  const handleViewTransfer = (transfer: StockTransfer) => {
    setSelectedTransfer(transfer);
    const items = getTransferItems(transfer.id);
    setSelectedTransferItems(items);
    setIsDrawerOpen(true);
  };

  const handleContinueTransfer = () => {
    if (!selectedTransfer) return;
    
    // Load the transfer configuration
    const config = getTransferConfig(selectedTransfer.id);
    
    if (!config) {
      toast.error("Could not load transfer configuration. This transfer may have been created before the edit feature was added.");
      return;
    }
    
    // Close drawer
    setIsDrawerOpen(false);
    
    // Navigate to step 2 with the configuration data
    navigateTo("stock-transfers/create/step2", { 
      setupData: {
        reference: config.reference,
        date: config.date,
        notes: config.notes,
        selectedItems: config.selectedItems,
      },
      configData: config,
      isEditing: true,
      transferId: selectedTransfer.id,
    });
    
    toast.success("Resuming transfer editing...");
  };

  const handleEditTransfer = () => {
    if (!selectedTransfer) return;
    
    // Load the transfer configuration
    const config = getTransferConfig(selectedTransfer.id);
    
    if (!config) {
      toast.error("Could not load transfer configuration. This transfer may have been created before the edit feature was added.");
      return;
    }
    
    // Close drawer
    setIsDrawerOpen(false);
    
    // Navigate to step 2 with the configuration data
    navigateTo("stock-transfers/create/step2", { 
      setupData: {
        reference: config.reference,
        date: config.date,
        notes: config.notes,
        selectedItems: config.selectedItems,
      },
      configData: config,
      isEditing: true,
      transferId: selectedTransfer.id,
    });
    
    toast.success("Editing transfer...");
  };

  const handleDeleteTransfer = () => {
    // Show confirmation dialog
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteTransfer = () => {
    if (!selectedTransfer) return;
    
    try {
      deleteTransfer(selectedTransfer.id);
      toast.success(`Transfer ${selectedTransfer.reference} deleted successfully`);
      
      // Close dialog and drawer
      setIsDeleteDialogOpen(false);
      setIsDrawerOpen(false);
      setSelectedTransfer(null);
      
      // Reload transfers
      reloadTransfers();
    } catch (error) {
      console.error("Error deleting transfer:", error);
      toast.error("Failed to delete transfer");
      setIsDeleteDialogOpen(false);
    }
  };

  const handleDuplicateTransfer = (transfer: StockTransfer) => {
    try {
      const duplicatedTransfer = duplicateTransfer(transfer.id);
      
      if (duplicatedTransfer) {
        toast.success(
          <div>
            <div className="font-semibold">Transfer duplicated successfully!</div>
            <div className="text-sm mt-1">
              Created {duplicatedTransfer.reference} as a draft
            </div>
          </div>
        );
        
        // Reload transfers
        reloadTransfers();
        
        // Close drawer if open
        if (isDrawerOpen) {
          setIsDrawerOpen(false);
          setSelectedTransfer(null);
        }
      } else {
        toast.error("Failed to duplicate transfer. Configuration data may be missing.");
      }
    } catch (error) {
      console.error("Error duplicating transfer:", error);
      toast.error("Failed to duplicate transfer");
    }
  };

  const handleShipTransfer = () => {
    if (!selectedTransfer) return;
    
    try {
      const updatedTransfer = updateTransferStatus(selectedTransfer.id, "in-transit");
      
      if (updatedTransfer) {
        toast.success(
          <div>
            <div className="font-semibold">Transfer shipped successfully!</div>
            <div className="text-sm mt-1">
              {selectedTransfer.reference} is now in transit
            </div>
          </div>
        );
        
        // Close drawer
        setIsDrawerOpen(false);
        setSelectedTransfer(null);
        
        // Reload transfers
        reloadTransfers();
      } else {
        toast.error("Failed to ship transfer");
      }
    } catch (error) {
      console.error("Error shipping transfer:", error);
      toast.error("Failed to ship transfer");
    }
  };

  const handleReceiveTransfer = () => {
    if (!selectedTransfer) return;
    
    try {
      const updatedTransfer = updateTransferStatus(selectedTransfer.id, "completed");
      
      if (updatedTransfer) {
        toast.success(
          <div>
            <div className="font-semibold">Transfer received successfully!</div>
            <div className="text-sm mt-1">
              {selectedTransfer.itemCount} items received ({selectedTransfer.totalQuantity} units)
            </div>
            <div className="text-sm">Reference: {selectedTransfer.reference}</div>
          </div>
        );
        
        // Close drawer
        setIsDrawerOpen(false);
        setSelectedTransfer(null);
        
        // Reload transfers
        reloadTransfers();
      } else {
        toast.error("Failed to receive transfer");
      }
    } catch (error) {
      console.error("Error receiving transfer:", error);
      toast.error("Failed to receive transfer");
    }
  };

  const handleDrawerClose = (open: boolean) => {
    setIsDrawerOpen(open);
    if (!open) {
      setSelectedTransfer(null);
      setSelectedTransferItems([]);
    }
  };

  const handleRowClick = (transfer: StockTransfer) => {
    handleViewTransfer(transfer);
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Sorting handler
  const handleSort = (column: keyof StockTransfer) => {
    if (sortConfig.column === column) {
      setSortConfig({
        column,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSortConfig({ column, direction: "asc" });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTransferLocations = (transferId: string) => {
    const items = getTransferItems(transferId);
    const locations = new Set<string>();
    
    items.forEach((item) => {
      locations.add(item.sourceLocation);
      locations.add(item.destinationLocation);
      
      // Add distribution destinations if they exist
      if (item.distributions) {
        item.distributions.forEach((dist) => {
          locations.add(dist.destination);
        });
      }
    });
    
    return Array.from(locations);
  };

  // Column definitions for DataTable
  const columns: ColumnDefinition<StockTransfer>[] = [
    {
      id: "reference",
      header: "Reference",
      sortable: true,
      cell: (transfer) => <span className="font-medium">{transfer.reference}</span>,
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      cell: (transfer) => (
        <Badge variant="outline" className={getStatusColor(transfer.status)}>
          <span className="flex items-center gap-1">
            {getStatusIcon(transfer.status)}
            {formatStatus(transfer.status)}
          </span>
        </Badge>
      ),
    },
    {
      id: "id",
      header: "Locations",
      sortable: false,
      cell: (transfer) => (
        <div className="flex flex-wrap gap-1">
          {getTransferLocations(transfer.id).map((location, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">
              {location}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: "createdBy",
      header: "Created By",
      sortable: true,
      cell: (transfer) => <span>{transfer.createdBy}</span>,
    },
    {
      id: "createdAt",
      header: "Created At",
      sortable: true,
      cell: (transfer) => (
        <span className="text-muted-foreground">{formatDate(transfer.createdAt)}</span>
      ),
    },
    {
      id: "itemCount",
      header: "Items",
      sortable: true,
      cell: (transfer) => <span>{transfer.itemCount}</span>,
    },
    {
      id: "totalQuantity",
      header: "Total Qty",
      sortable: true,
      cell: (transfer) => <span>{transfer.totalQuantity}</span>,
    },
    {
      id: "notes",
      header: "Notes",
      sortable: false,
      cell: (transfer) => (
        <span className="max-w-[200px] truncate text-muted-foreground">
          {transfer.notes || "-"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Stock Transfers</h1>
          <p className="text-muted-foreground mt-1">
            Transfer stock between locations and manage transfer records
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton onClick={toggleHelp} variant="outline" size="default" showLabel />
          <Button onClick={handleCreateTransfer} size="default">
            <Plus className="h-4 w-4 mr-2" />
            Create Transfer
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <StaticMetricCardAlt
          title="Draft Transfers"
          value={metrics.drafts.toString()}
          icon={Edit}
          trend="neutral"
        />
        <StaticMetricCardAlt
          title="In Transit"
          value={metrics.inTransit.toString()}
          icon={ArrowRightLeft}
          trend="neutral"
        />
        <StaticMetricCardAlt
          title="Completed"
          value={metrics.completed.toString()}
          icon={CheckCircle2}
          trend="neutral"
        />
        <StaticMetricCardAlt
          title="Total Items"
          value={metrics.totalItems.toString()}
          icon={Package}
          trend="neutral"
        />
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Transfer Records</CardTitle>
              <CardDescription>View and manage all stock transfers</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Tabs and Search */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-auto">
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="draft">Draft</TabsTrigger>
                  <TabsTrigger value="in-transit">In Transit</TabsTrigger>
                  <TabsTrigger value="completed">Completed</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="flex items-center gap-2">
                <div className="relative w-64">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search transfers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <div className="relative w-64">
                  <MapPin className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Filter by location..."
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
            </div>

            {/* Table */}
            <DataTable
              data={paginatedTransfers}
              columns={columns}
              sortConfig={sortConfig}
              onSort={handleSort}
              currentPage={currentPage}
              totalPages={Math.ceil(sortedTransfers.length / itemsPerPage)}
              itemsPerPage={itemsPerPage}
              totalItems={sortedTransfers.length}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onRowClick={handleRowClick}
              emptyState={
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    {searchQuery || locationFilter
                      ? "No transfers found matching your filters"
                      : activeTab === "all"
                      ? "No transfers yet. Create your first transfer to get started."
                      : `No ${activeTab} transfers`}
                  </p>
                </div>
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Transfer Details Drawer */}
      <Sheet open={isDrawerOpen} onOpenChange={handleDrawerClose}>
        <SheetContent side="right" className="w-full sm:w-[800px] sm:max-w-[800px] p-0 flex flex-col">
          {selectedTransfer && (
            <>
              {/* Header */}
              <SheetHeader className="bg-muted p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <SheetTitle className="text-2xl font-semibold">
                      {selectedTransfer.reference}
                    </SheetTitle>
                    <SheetDescription className="text-sm text-muted-foreground mt-1">
                      Transfer ID: {selectedTransfer.id}
                    </SheetDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={getStatusColor(selectedTransfer.status)}>
                      <span className="flex items-center gap-1">
                        {getStatusIcon(selectedTransfer.status)}
                        {formatStatus(selectedTransfer.status)}
                      </span>
                    </Badge>
                  </div>
                </div>
              </SheetHeader>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-4 bg-background">
                <StockTransferDrawerDetails
                  transfer={selectedTransfer}
                  transferItems={selectedTransferItems.map(item => ({
                    itemCode: item.itemCode,
                    itemName: item.itemName,
                    sourceLocation: item.sourceLocation,
                    destinationLocation: item.destinationLocation,
                    quantity: item.quantity,
                  }))}
                  onContinue={handleContinueTransfer}
                  onEdit={handleEditTransfer}
                  onDelete={handleDeleteTransfer}
                  onShipTransfer={handleShipTransfer}
                  onDuplicate={() => handleDuplicateTransfer(selectedTransfer)}
                  onReceive={handleReceiveTransfer}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Transfer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete transfer <strong>{selectedTransfer?.reference}</strong>?
              This action cannot be undone and will permanently remove the transfer and all its items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTransfer}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Contextual Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
        onNavigateToPage={(pageId) => navigateTo(pageId)}
      />
    </div>
  );
}
